import {tokens, colors, radii} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import checkIcon from '@helios/shared/images/icons/check-black.svg'
import {Text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

interface Props extends Omit<JSX.IntrinsicElements['input'], 'ref' | 'onChange' | 'type' | 'css'> {
    label?: RichTextProps['content']
    onChange?: (checked: boolean) => void
    error?: string | null | boolean
    'data-testid'?: string
    showErrorLabel?: boolean
}

const Checkbox = ({label, onChange, checked, error, showErrorLabel = true, ...props}: Props) => {
    const id = React.useId()
    const hasLabel = hasRichText(label)
    const shouldShowError = error && showErrorLabel

    return (
        <Wrapper>
            <CheckboxInput
                id={id}
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                {...props}
            />

            <StyledCheckbox htmlFor={id} $checked={checked} $hasError={!!error} />

            {(hasLabel || error) && (
                <div style={{display: 'grid'}}>
                    {hasLabel && (
                        <RichText
                            as="label"
                            htmlFor={id}
                            variant="label-base"
                            color="muted"
                            content={label}
                        />
                    )}

                    {hasLabel && shouldShowError && ' '}

                    {shouldShowError && (
                        <Text
                            as="span"
                            variant="label-base"
                            color="error"
                            {...(props['data-testid'] && {
                                'data-testid': `${props['data-testid']}-error`,
                            })}
                        >
                            {error}
                        </Text>
                    )}
                </div>
            )}
        </Wrapper>
    )
}

const Wrapper = styled.div`
    position: relative;
    display: grid;
    gap: ${tokens.spacing12};
    grid-auto-flow: column;
    justify-content: start;
`

const CheckboxInput = styled.input`
    position: absolute;
    pointer-events: none;
    opacity: 0;
`

interface StyledCheckboxProps {
    $checked?: boolean
    $hasError?: boolean
}

const StyledCheckbox = styled.label<StyledCheckboxProps>`
    display: flex;
    width: 16px;
    height: 16px;
    margin-top: ${tokens.spacing2};
    cursor: pointer;
    background-image: ${(props) => props.$checked && `url(${checkIcon}) `};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 12px;
    border: 2px solid ${colors.border.secondary};
    border-color: ${(props) => props.$hasError && colors.border.error};
    border-radius: ${radii.arc4};
    justify-content: center;
    align-items: center;

    ${CheckboxInput}:focus + & {
        ${focusStyles()}
    }
`

export default Checkbox
